import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * AuthCallback handles the OAuth callback from Google, Facebook, and Apple.
 * Detects the provider from the 'state' parameter and exchanges the code for user data.
 * For Apple: The backend handles the OAuth flow and redirects here with session_token.
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginWithGoogle } = useApp();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Use useRef to prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // If this OAuth run was started from the native app, hand the session
      // back to it via the app's deep-link scheme instead of navigating.
      const bridgeToNativeIfNeeded = (u, token) => {
        try {
          if (sessionStorage.getItem('mobile_auth') === '1') {
            sessionStorage.removeItem('mobile_auth');
            const payload = encodeURIComponent(JSON.stringify(u || {}));
            window.location.href = `mobile://auth?token=${encodeURIComponent(token || '')}&user=${payload}`;
            return true;
          }
        } catch (_) { /* ignore */ }
        return false;
      };
      try {
        // Get parameters from URL
        const code = searchParams.get('code');
        const state = searchParams.get('state'); // 'google', 'facebook', or 'apple'
        const idToken = searchParams.get('id_token');
        const sessionToken = searchParams.get('session_token'); // For Apple redirect flow
        const email = searchParams.get('email'); // For Apple redirect flow
        const provider = searchParams.get('provider'); // For Apple redirect flow
        const errorParam = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        // Handle OAuth errors
        if (errorParam) {
          console.error('OAuth error:', errorParam, errorDescription);
          setError(errorDescription || errorParam || 'Authorization was denied');
          return;
        }

        // Handle Apple redirect flow (backend already processed OAuth, just need to login)
        if (state === 'apple' && sessionToken && email) {
          // Fetch user data from backend using session token
          try {
            const userResponse = await axios.get(`${API}/api/user/session`, {
              headers: { 'Authorization': `Bearer ${sessionToken}` }
            });

            if (userResponse.data.success && userResponse.data.user) {
              await loginWithGoogle(userResponse.data.user, sessionToken);
            } else {
              // Create a basic user object from the email
              await loginWithGoogle({
                email: email,
                name: '',
                authProvider: 'apple'
              }, sessionToken);
            }
          } catch (err) {
            // Fallback: create user object from email
            await loginWithGoogle({
              email: email,
              name: '',
              authProvider: 'apple'
            }, sessionToken);
          }
          
          // Get redirect path from session storage or default to dashboard
          const redirectPath = sessionStorage.getItem('authRedirect') || '/dashboard';
          sessionStorage.removeItem('authRedirect');

          if (bridgeToNativeIfNeeded({ email, authProvider: 'apple' }, sessionToken)) return;
          navigate(redirectPath, { replace: true });
          return;
        }

        if (!code && !idToken) {
          console.error('No authorization code found in URL');
          setError('No authorization code received. Please try again.');
          return;
        }

        if (!state || (state !== 'google' && state !== 'facebook' && state !== 'apple')) {
          console.error('Invalid or missing state parameter:', state);
          setError('Invalid authentication state. Please try again.');
          return;
        }

        // Determine the callback endpoint based on the provider
        let callbackEndpoint;
        if (state === 'google') {
          callbackEndpoint = `${API}/api/auth/google/callback`;
        } else if (state === 'facebook') {
          callbackEndpoint = `${API}/api/auth/facebook/callback`;
        } else {
          callbackEndpoint = `${API}/api/auth/apple/callback`;
        }

        // Get the redirect URI (must match what was sent to OAuth provider)
        const redirectUri = `${window.location.origin}/auth/callback`;

        // Exchange code for user data via backend
        const response = await axios.post(callbackEndpoint, {
          code: code,
          id_token: idToken,
          redirect_uri: redirectUri
        });

        if (response.data.success && response.data.user) {
          // Store session token if provided and pass it through so the
          // AppContext axios interceptor picks it up.
          const oauthSessionToken = response.data.session_token || null;

          // Login the user using the existing context function
          await loginWithGoogle(response.data.user, oauthSessionToken);
          
          // Get redirect path from session storage or default to dashboard
          const redirectPath = sessionStorage.getItem('authRedirect') || '/dashboard';
          sessionStorage.removeItem('authRedirect');

          if (bridgeToNativeIfNeeded(response.data.user, oauthSessionToken)) return;
          // Redirect to the intended destination
          navigate(redirectPath, { replace: true });
        } else {
          console.error('Authentication failed:', response.data);
          setError('Authentication failed. Please try again.');
        }
      } catch (err) {
        console.error('Auth callback error:', err);
        const errorMessage = err.response?.data?.detail || err.message || 'Authentication failed';
        setError(errorMessage);
      }
    };

    processAuth();
  }, [navigate, loginWithGoogle, searchParams]);

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-neutral-900 rounded-2xl p-8 shadow-lg max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-500/15 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-neutral-100 mb-2">Authentication Failed</h2>
          <p className="text-gray-600 dark:text-neutral-400 mb-6">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="w-full py-3 bg-black text-white rounded-xl font-bold hover:bg-gray-800 transition-colors"
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-12 h-12 animate-spin text-black dark:text-white mx-auto mb-4" />
        <p className="text-gray-600 dark:text-neutral-400 font-medium">Signing you in...</p>
        <p className="text-gray-400 dark:text-neutral-500 text-sm mt-2">Please wait while we verify your account</p>
      </div>
    </div>
  );
};

export default AuthCallback;
